<!-- Backend #579 (slice 3): the signed AI evaluation — second sign-off guard for Live VAT. -->
<p class="text-muted mt-2" i18n>
  Die KI-Stufe der MwSt.-Ermittlung darf erst produktiv Sätze anwenden, wenn ihre Trefferquote
  gemessen und freigegeben ist. Die Freigabe gilt genau für das laufende Modell und die
  laufende Prompt-Version: Ein Modellwechsel oder eine Prompt-Änderung erzeugt eine neue
  Version, und die MwSt.-Ermittlung lässt sich erst wieder produktiv schalten, wenn diese
  erneut ausgewertet und freigegeben ist. Ein produktives Profil wendet die KI nur oberhalb
  einer Konfidenzschwelle an, die mindestens so hoch ist wie die hier freigegebene.
</p>

@if (status) {
  <div class="border rounded p-3 mb-3 bg-light">
    <div class="row g-2">
      <div class="col-sm-3">
        <div class="small text-muted" i18n>Laufendes Modell</div>
        <div class="fw-semibold">{{ status.currentModel }}</div>
      </div>
      <div class="col-sm-3">
        <div class="small text-muted" i18n>Prompt-Version</div>
        <div class="fw-semibold font-monospace">{{ status.currentPromptVersion }}</div>
      </div>
      <div class="col-sm-2">
        <div class="small text-muted" i18n>KI-Stufe</div>
        <div class="fw-semibold">
          @if (status.aiEnabled) {
            <span class="badge text-bg-success" i18n>aktiv</span>
          } @else {
            <span class="badge text-bg-secondary" i18n>inaktiv</span>
          }
        </div>
      </div>
      <div class="col-sm-4">
        <div class="small text-muted" i18n>Freigabe</div>
        <div class="fw-semibold">
          @if (status.accepted && status.acceptance) {
            <span class="badge text-bg-success" i18n>freigegeben</span>
            <span class="ms-2" i18n>
              ab {{ status.acceptance.threshold | percent: '1.0-2' }} Konfidenz,
              Trefferquote {{ status.acceptance.measuredPrecision | percent: '1.0-2' }}
              bei {{ status.acceptance.sampleSize }} Stichproben
            </span>
          } @else {
            <span class="badge text-bg-warning" i18n>nicht freigegeben</span>
            <span class="ms-2 text-muted" i18n>
              Ein produktives MwSt.-Profil mit KI-Stufe wird abgelehnt.
            </span>
          }
        </div>
      </div>
    </div>
    @if (!status.aiEnabled) {
      <div class="small text-muted mt-2" i18n>
        Ohne aktive KI-Stufe (kein API-Schlüssel oder abgeschaltet) läuft keine Auswertung —
        die Freigabe kann trotzdem für die konfigurierte Modell- und Prompt-Version erfolgen.
      </div>
    }
  </div>
}

@if (signForm) {
  <form [formGroup]="signForm" class="row g-2 align-items-end border rounded p-3 mb-3">
    <div class="col-12">
      <span class="fw-semibold" i18n>KI-Auswertung freigeben</span>
      <div class="small text-muted" i18n>
        Trefferquote der KI bei Antworten mit einer Konfidenz ab der Schwelle, gemessen an
        einer Stichprobe fachlich geprüfter Artikel. Die Prompt-Version ist auf die laufende
        festgelegt.
      </div>
    </div>
    <div class="col-sm-3">
      <label class="form-label col-form-label-sm mb-0" i18n>Prompt-Version</label>
      <input type="text" formControlName="promptVersion" class="form-control form-control-sm font-monospace" />
    </div>
    <div class="col-sm-2">
      <label class="form-label col-form-label-sm mb-0" i18n>Schwelle (%)</label>
      <input
        type="number"
        formControlName="thresholdPercent"
        class="form-control form-control-sm"
        min="0.01"
        max="100"
        step="1"
        [ngClass]="{
          'is-invalid':
            f['thresholdPercent'].invalid && (f['thresholdPercent'].dirty || f['thresholdPercent'].touched)
        }"
      />
    </div>
    <div class="col-sm-2">
      <label class="form-label col-form-label-sm mb-0" i18n>Stichprobe</label>
      <input
        type="number"
        formControlName="sampleSize"
        class="form-control form-control-sm"
        min="1"
        step="1"
        [ngClass]="{
          'is-invalid': f['sampleSize'].invalid && (f['sampleSize'].dirty || f['sampleSize'].touched)
        }"
      />
    </div>
    <div class="col-sm-2">
      <label class="form-label col-form-label-sm mb-0" i18n>Trefferquote (%)</label>
      <input
        type="number"
        formControlName="precisionPercent"
        class="form-control form-control-sm"
        min="0"
        max="100"
        step="0.1"
        [ngClass]="{
          'is-invalid':
            f['precisionPercent'].invalid && (f['precisionPercent'].dirty || f['precisionPercent'].touched)
        }"
      />
    </div>
    <div class="col-12">
      <label class="form-label col-form-label-sm mb-0" i18n>Notizen</label>
      <textarea
        formControlName="notes"
        class="form-control form-control-sm"
        rows="2"
        i18n-placeholder
        placeholder="z. B. Stichprobe, Prüfer, Auffälligkeiten"
      ></textarea>
    </div>
    <div class="col-12 col-sm-auto d-flex gap-2">
      <button
        class="btn btn-primary btn-sm flex-shrink-0"
        type="button"
        [asyncClick]="submitForm.bind(this)"
        [disabled]="signForm.invalid"
      >
        <span i18n>Freigabe unterzeichnen</span>
      </button>
    </div>
  </form>
}

<kendo-grid [data]="rows" [sortable]="false" [pageable]="false">
  <ng-template kendoGridNoRecordsTemplate>
    <app-grid-no-records i18n-emptyText emptyText="Noch keine Freigabe unterzeichnet"></app-grid-no-records>
  </ng-template>
  <kendo-grid-column field="verifiedAt" i18n-title title="Freigegeben am" [width]="150">
    <ng-template kendoGridCellTemplate let-dataItem>
      {{ dataItem.verifiedAt | date: 'short' }}
      @if (dataItem.isCurrent) {
        <span class="badge text-bg-success ms-1" i18n>aktuell</span>
      }
    </ng-template>
  </kendo-grid-column>
  <kendo-grid-column field="model" i18n-title title="Modell" [width]="170"></kendo-grid-column>
  <kendo-grid-column field="promptVersion" i18n-title title="Prompt-Version" [width]="150">
    <ng-template kendoGridCellTemplate let-dataItem>
      <span class="font-monospace">{{ dataItem.promptVersion }}</span>
    </ng-template>
  </kendo-grid-column>
  <kendo-grid-column field="threshold" i18n-title title="Schwelle" [width]="100">
    <ng-template kendoGridCellTemplate let-dataItem>
      {{ dataItem.threshold | percent: '1.0-2' }}
    </ng-template>
  </kendo-grid-column>
  <kendo-grid-column field="sampleSize" i18n-title title="Stichprobe" [width]="100"></kendo-grid-column>
  <kendo-grid-column field="measuredPrecision" i18n-title title="Trefferquote" [width]="110">
    <ng-template kendoGridCellTemplate let-dataItem>
      {{ dataItem.measuredPrecision | percent: '1.0-2' }}
    </ng-template>
  </kendo-grid-column>
  <kendo-grid-column field="notes" i18n-title title="Notizen"></kendo-grid-column>
</kendo-grid>
